import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import localizedStrings from 'localization';
import { ShowcaseItem } from 'modules/profile/typings';
import { selectPublishedShowcases, selectShowcaseTemplateByDefault } from 'modules/profile/selectors';
import {
  ProfileSection,
  ProfileSectionPanel,
  ProfileSectionTitle,
  PublishedShowcaseEmpty,
  PublishedShowcasesContainer,
} from 'modules/profile/components';
import ShareShowcaseModal from './ShareShowcaseModal';
import { SHOWCASE } from 'routes';
import ButtonPrimary from 'components/button-primary';
import PublishedShowcaseItem from './PublishedShowcaseItem';
import DeleteShowcaseModal from './DeleteShowcaseModal';
import { createShowcase, getPublishedShowcases } from 'modules/profile/actions';
import useActionStatus from 'hooks/useActionStatus';
import { selectActiveQuest, selectLoadingAdditionalFinished } from 'modules/loading-screen/quests/selectors';
import { CenterLoader } from 'components/loading-spinner/components';
import LoadingSpinner from 'components/loading-spinner';
import theme from 'utils/theme';
import EditShowcaseModal from './edit-showcase-modal';
import { hasMinimunAppletsCompletedForShowcase } from 'modules/applets/selectors';
import { FieldErrorMessage } from 'components/input-field/components';
import { clearShowcase } from 'modules/showcase/actions';
import { AppDispatch } from 'redux/typings';
import LoadingBar from 'components/loading-bar';

type ModalState = {
  createShowcaseOpen?: boolean;
  editShowcaseOpen?: boolean;
  shareShowcaseOpen?: boolean;
  deleteShowcaseOpen?: boolean;
  showcaseId?: string;
};

const PublishedShowcases = () => {
  const lastItemRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [modalState, setModalState] = useState<ModalState>({});
  const publishedShowcases = useSelector(selectPublishedShowcases);
  const loadingFinished = useSelector(selectLoadingAdditionalFinished);
  const hasMinimunAppletsCompleted = useSelector(hasMinimunAppletsCompletedForShowcase);
  const [isPendingCohorts, wasFulfilledCohorts] = useActionStatus(getPublishedShowcases);
  const [isCreating] = useActionStatus(createShowcase);
  const activeQuest = useSelector(selectActiveQuest);

  const shouldShowLoader = useMemo(() =>
    !loadingFinished || isPendingCohorts || !wasFulfilledCohorts
    , [loadingFinished, isPendingCohorts, wasFulfilledCohorts]);

  const questShowcaseTemplate = useSelector(selectShowcaseTemplateByDefault);

  const handleCreateShowcase = useCallback(() => {
    if (activeQuest) {
      dispatch(createShowcase({
        activeQuestContentfulId: activeQuest.contentfulId,
        activeQuestId: activeQuest.id,
        showcaseTemplateId: questShowcaseTemplate?.qst_id
      }));
    }
  }, [dispatch, activeQuest, questShowcaseTemplate]);

  const openEditShowcaseClick = useCallback((showcaseId: string) => {
    setModalState({ editShowcaseOpen: true, showcaseId });
  }, []);

  const openShareShowcaseModal = useCallback((showcaseId: string) => {
    setModalState({ shareShowcaseOpen: true, showcaseId });
  }, []);

  const openDeleteShowcaseModal = useCallback((showcaseId: string) => {
    setModalState({ deleteShowcaseOpen: true, showcaseId });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({});
  }, []);

  const navigateToShowcase = useCallback((showcaseId: string) => {
    dispatch(clearShowcase())
    window.open(`${SHOWCASE}/${showcaseId}`);
  }, [dispatch]);

  const creationAndScroll = async () => {
    await handleCreateShowcase()
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      lastItemRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);

    return () => clearTimeout(timeout);
  }, [publishedShowcases?.length]);


  return (
    <ProfileSection>
      <ProfileSectionTitle>{localizedStrings.showcases}</ProfileSectionTitle>

      <ProfileSectionPanel>
        <EditShowcaseModal
          open={modalState.editShowcaseOpen ?? false}
          closeModal={closeModal}
          showcaseId={modalState.showcaseId ?? ''}
        />
        <ShareShowcaseModal
          open={modalState.shareShowcaseOpen ?? false}
          closeModal={closeModal}
          showcaseId={modalState.showcaseId ?? ''}
        />
        <DeleteShowcaseModal
          open={modalState.deleteShowcaseOpen ?? false}
          closeModal={closeModal}
          showcaseId={modalState.showcaseId ?? ''}
        />

        {
          shouldShowLoader ?
            <CenterLoader>
              <LoadingSpinner size={50} color={theme.colors.blue1[100]} />
            </CenterLoader>
            :
            <>
              <PublishedShowcasesContainer>
                {publishedShowcases && publishedShowcases.length > 0 ?

                  publishedShowcases.map((publishedShowcase: ShowcaseItem, index) => {
                    const isLast = index === publishedShowcases.length - 1;
                    return (
                      <div ref={isLast ? lastItemRef : null} key={publishedShowcase.id}>
                        <PublishedShowcaseItem
                          showcase={publishedShowcase}
                          index={index}
                          maxIndex={publishedShowcases.length - 1}
                          onEditShowcaseClick={openEditShowcaseClick}
                          onDeleteShowcaseClick={openDeleteShowcaseModal}
                          onShareShowcaseClick={openShareShowcaseModal}
                          onViewShowcaseClick={navigateToShowcase}
                        />
                      </div>
                    );
                  })
                  : (
                    <>
                      <PublishedShowcaseEmpty>
                        {localizedStrings.noPublishedShowcases}
                      </PublishedShowcaseEmpty>
                    </>
                  )}



                {!hasMinimunAppletsCompleted ?
                  <FieldErrorMessage>
                    {localizedStrings.showcaseAppletsError}
                  </FieldErrorMessage>
                  : null}
              </PublishedShowcasesContainer>

              {isCreating && <LoadingBar />}

              <ButtonPrimary
                data-testid="ButtonPrimary_createShowcase"
                style={{ marginTop: 15 }}
                disabled={!hasMinimunAppletsCompleted || isCreating} onClick={creationAndScroll}>
                {isCreating ? localizedStrings.loading : localizedStrings.createShowcase}
              </ButtonPrimary>
            </>
        }

      </ProfileSectionPanel>
    </ProfileSection>
  );
};

export default PublishedShowcases;
